import React, { ReactNode, useMemo } from 'react';
import { styled } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { AccessLevel } from '@bimdata/definitions/const';

import ModalDialog, { ModalDlgProps } from '@/components/ModalDialog';
import TableList, { ColumnMeta } from '@/components/TableList';
import t from '@/utils/translate';


type Permission = 'view' | 'models' | 'participants' | 'setup' | 'delete' | 'pass';

const HELP_ROWS: Permission[] = [ 'view', 'models', 'participants', 'setup', 'delete', 'pass' ];

const HelpDialog = styled(ModalDialog)({
  ['& div.MuiDialog-paper']: {
    borderRadius: '10px',
  },
  ['& div.MuiDialogContent-root']: {
    paddingTop: '16px',
  }
});

const PermissionInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '0px',
  gap: '2px',
  margin: '0px',
});

const PermissionTitle = styled('div')({
  color: '#091E42',
  fontSize: '14px',
  fontWeight: 500,
});

const PermissionDescription = styled('div')({
  color: '#6B778C',
  fontSize: '14px',
  fontWeight: 300,
  textWrap: 'pretty',
});

const GRANTS: Record<AccessLevel, Record<Permission, boolean>> = {
  [AccessLevel.OWNER]: {
    view: true,
    models: true,
    participants: true,
    setup: true,
    delete: true,
    pass: true,
  },
  [AccessLevel.ADMIN]: {
    view: true,
    models: true,
    participants: true,
    setup: true,
    delete: false,
    pass: false,
  },
  [AccessLevel.WRITER]: {
    view: true,
    models: true,
    participants: false,
    setup: false,
    delete: false,
    pass: false,
  },
  [AccessLevel.READER]: {
    view: true,
    models: false,
    participants: false,
    setup: false,
    delete: false,
    pass: false,
  },
}

const Granted = styled(CheckCircleOutlinedIcon)({
  color: '#00875A',
  width: '24px',
  height: '24px',
});

const Forbidden = styled(CancelOutlinedIcon)({
  color: '#DE350B',
  width: '24px',
  height: '24px',
});

function grantStatus(status: boolean): ReactNode {
  return status ? <Granted /> : <Forbidden />;
}

const GrantsTable = styled(TableList)({
  width: '678px',
  ['& td']: {
    padding: '10px',
  }
});

export default (props: ModalDlgProps) => {
  const HELP_COLUMNS: ColumnMeta[] = useMemo<ColumnMeta[]>(() => [
    {
      title: t('permissions.role.title'),
      noSort: true,
      dataGetter: (item: Permission) => {
        const descCode = `permissions.${item}.description`;
        const desc = t(descCode);
        return (
        <PermissionInfo>
          <PermissionTitle>{t(`permissions.${item}.title`)}</PermissionTitle>
          {(desc && desc !== descCode) && <PermissionDescription>{desc}</PermissionDescription>}
        </PermissionInfo>
        );
      },
    },
    {
      title: t('permissions.roles.owner.title'),
      noSort: true,
      width: 80,
      cellStyle: {
        textAlign: 'center',
      },
      dataGetter: (item: Permission) => grantStatus(GRANTS[AccessLevel.OWNER][item]),
    },
    {
      title: t('permissions.roles.admin.title'),
      noSort: true,
      width: 80,
      cellStyle: {
        textAlign: 'center',
      },
      dataGetter: (item: Permission) => grantStatus(GRANTS[AccessLevel.ADMIN][item]),
    },
    {
      title: t('permissions.roles.writer.title'),
      noSort: true,
      width: 80,
      cellStyle: {
        textAlign: 'center',
      },
      dataGetter: (item: Permission) => grantStatus(GRANTS[AccessLevel.WRITER][item]),
    },
    {
      title: t('permissions.roles.reader.title'),
      noSort: true,
      width: 80,
      cellStyle: {
        textAlign: 'center',
      },
      dataGetter: (item: Permission) => grantStatus(GRANTS[AccessLevel.READER][item]),
    },
    {
      title: t('permissions.roles.share.title'),
      noSort: true,
      width: 80,
      cellStyle: {
        textAlign: 'center',
      },
      dataGetter: (item: Permission) => grantStatus(GRANTS[AccessLevel.READER][item]),
    }
  ], []);

  return (
    <HelpDialog {...props} title={t('permissions.title')}>
      <GrantsTable
        columns={HELP_COLUMNS}
        data={HELP_ROWS}
      />
    </HelpDialog>
  );
};
